/**
 * Upload module for Vuex store
 */
import ApiService from '@/services/ApiService';
import WaveformService from '@/services/WaveformService';

export default {
  namespaced: true,
  
  state: {
    highQualityFile: '',
    regularQualityFile: '',
    isUploading: false,
    // User-facing message for the last failed single-track upload (null when the last one succeeded)
    uploadError: null,
  },
  
  getters: {
    highQualityFile: state => state.highQualityFile,
    regularQualityFile: state => state.regularQualityFile,
    isUploading: state => state.isUploading,
    uploadError: state => state.uploadError,
  },
  
  mutations: {
    SET_HIGH_QUALITY_FILE(state, file) {
      state.highQualityFile = file;
    },
    SET_REGULAR_QUALITY_FILE(state, file) {
      state.regularQualityFile = file;
    },
    SET_IS_UPLOADING(state, isUploading) {
      state.isUploading = isUploading;
    },
    SET_UPLOAD_ERROR(state, message) {
      state.uploadError = message;
    },
  },
  
  actions: {
    setHighQualityFile({ commit }, file) {
      commit('SET_HIGH_QUALITY_FILE', file);
    },
    
    setRegularQualityFile({ commit }, file) {
      commit('SET_REGULAR_QUALITY_FILE', file);
    },
    
    setIsUploading({ commit }, isUploading) {
      commit('SET_IS_UPLOADING', isUploading);
    },
    
    async uploadFiles({ commit, state, dispatch }, fileInfo) {
      if (!state.highQualityFile || !state.regularQualityFile || !fileInfo || !fileInfo.title) {
        console.error('Cannot upload files: invalid parameters');
        return false;
      }

      commit('SET_UPLOAD_ERROR', null);
      commit('SET_IS_UPLOADING', true);

      try {
        await ApiService.uploadFiles(
          fileInfo,
          state.highQualityFile,
          state.regularQualityFile
        );

        // A re-uploaded track keeps its name (and S3 key), so drop any waveform
        // cached for it — otherwise this session keeps drawing the old version.
        WaveformService.invalidate(fileInfo.title);

        // After successful upload, reload the master playlist
        await dispatch('playlist/loadMasterPlaylist', null, { root: true });
        commit('SET_IS_UPLOADING', false);
        return true;
      } catch (error) {
        console.error('Failed to upload files:', error);
        // Record the reason BEFORE flipping isUploading, so the dialog's watcher can tell a
        // failed upload from a successful one (it used to report success either way).
        commit('SET_UPLOAD_ERROR', error.userMessage || error.message || 'Upload failed');
        commit('SET_IS_UPLOADING', false);
        return false;
      }
    },

    async uploadBatchFile(context, fileData) {
      if (!fileData || !fileData.name || !fileData.year || !fileData.mp3File || !fileData.highQualityFile) {
        console.error('Cannot upload batch file: invalid parameters');
        throw new Error('Invalid batch upload parameters');
      }

      try {
        const fileInfo = {
          title: fileData.name,
          date: `${fileData.year}-01-01`, // Use January 1st of the selected year
        };

        await ApiService.uploadFiles(
          fileInfo,
          fileData.highQualityFile,
          fileData.mp3File,
          fileData.onProgress
        );

        // See uploadFiles: forget any waveform cached under this track name.
        WaveformService.invalidate(fileInfo.title);

        return true;
      } catch (error) {
        console.error(`Failed to upload batch file ${fileData.name}:`, error);
        throw error;
      }
    },
  }
};